import { paramsToQueryString, queryStringToParams } from '../../helpers/url';
import { getType } from './helpers/typeModifier';
import { getErrorMetadataObject } from 'Helpers/ssrRetry';

const initialStateBase = {
    collections: {},
    isFetching: {},
    isError: {},
    collectionMetadata: {},
    elements: {},
    errorsMetadata: {},
    similarAdsData: {},
    currentPageElements: {}
};

const changeStatus = (state, type, status, stasType) => {
    return {
        [stasType]: Object.assign({}, state[stasType], {
            [type]: status
        })
    };
};

const changeFetchingStatusNew = (state, type, status) => changeStatus(state, type, status, 'isFetching');
const changeErrorStatus = (state, type, status) => changeStatus(state, type, status, 'isError');

const changeErrorsMetadata = (state, type, action) => {
    return {
        errorsMetadata: {
            ...state.errorsMetadata,
            [type]: getErrorMetadataObject(action, state.errorsMetadata && state.errorsMetadata[type])
        }
    };
};

const getNewCollections = (state, type, data, alwaysAppend = false) => {
    const newCollections = {};

    if ((Array.isArray(data.data) && data.data.length && data.data[0].id)
    || (Array.isArray(data.similarads_data) && data.similarads_data.length && data.similarads_data[0].id)) {
        const collection = data.data?.map(element => element.id) || [];
        const similarAdscollection = data.similarads_data?.map(element => element.id);
        const metadata = state.collectionMetadata[type];
        const needsAppend = alwaysAppend || (metadata && (metadata.cursor || metadata.nextPageUrl));

        // if there is an existing collection for this url
        // and the needsAppend is ON
        if (Array.isArray(state.collections[type]) && needsAppend) {
            newCollections[type] = similarAdscollection ? [...state.collections[type], ...collection, similarAdscollection] : [...state.collections[type], ...collection];
        }
        else {
            newCollections[type] = similarAdscollection ? [...collection, similarAdscollection] : collection;
        }
    }
    else {
        newCollections[type] = data.data;
    }

    return {
        ...state.collections,
        ...newCollections
    };
};

const getNewElements = (state, array, getSimilarAds) => {
    if (!Array.isArray(array) || array.length === 0) {
        return getSimilarAds ? state.similarAdsData : state.elements;
    }

    const newElements = {};

    array.forEach(element => {
        newElements[element.id] = element;
    });

    const prevElements = getSimilarAds ? state.similarAdsData : state.elements;

    return {
        ...prevElements,
        ...newElements
    };
};

const getCurrentPageElements = array => {
    if (!Array.isArray(array) || array.length === 0) {
        return {};
    }

    const newElements = {};

    array.forEach(element => {
        newElements[element.id] = element;
    });

    return {
        ...newElements
    };
};

const getCurrentUserCreatedAt = (state, data) => {
    if (typeof data === 'object' && data !== null && !Array.isArray(data) && data?.created_at) {
        return data.created_at || null;
    }
    return state.userCreatedAt || null;
};

const getNextPageUrl = next_page_url => {
    if (next_page_url) {
        const params = next_page_url.slice(next_page_url.indexOf('?') + 1);
        const { clientId, clientVersion, ...rest } = queryStringToParams(params); // eslint-disable-line no-unused-vars

        return paramsToQueryString(rest);
    }
    return null;
};

const getNewMetadata = (state, type, data) => {
    const newMetadata = {};
    const aditionalMetadataKeys = state.aditionalMetadataKeys || [];

    // if passed in data has a attached metadata
    if (data.metadata) {
        const { cursor, total, total_ads, total_suggested_ads } = data.metadata;
        const nextPageUrl = getNextPageUrl(data.metadata.next_page_url);
        const nextSuggestedPageUrl = getNextPageUrl(data.metadata.suggested_next_page_url);
        const aditionalMetadata = aditionalMetadataKeys.reduce((memo, key) => {
            if ((key in data.metadata) && (typeof data.metadata[key] !== 'undefined')) {
                memo[key] = data.metadata[key];
            }
            return memo;
        }, {});

        newMetadata[type] = {
            cursor,
            total: total || total_ads,
            ...aditionalMetadata
        };
        if (nextPageUrl) {
            newMetadata[type].nextPageUrl = nextPageUrl;
        }

        if (nextSuggestedPageUrl) {
            newMetadata[type].nextSuggestedPageUrl = nextSuggestedPageUrl;
        }

        if (total_suggested_ads) {
            newMetadata[type].total_suggested_ads = total_suggested_ads;
        }
    }

    return {
        ...state.collectionMetadata,
        ...newMetadata
    };
};

const reducer = (state, action, type, initialStateCentres) => {
    let newState;

    let reducerType = getType(type, action.url);

    switch (action.type) {
        case `ERROR_${type.toUpperCase()}`:
        case `ERROR_POST_${type.toUpperCase()}`:
        case `ERROR_PUT_${type.toUpperCase()}`:
        case `ERROR_PATCH_${type.toUpperCase()}`:
        case `ERROR_POST_ELEMENT_${type.toUpperCase()}`:
        case `ERROR_PUT_ELEMENT_${type.toUpperCase()}`:
        case `ERROR_ELEMENT_${type.toUpperCase()}`: {
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, false),
                ...changeErrorStatus(state, reducerType, true),
                ...changeErrorsMetadata(state, reducerType, action)
            };
            break;
        }
        case `REQUEST_ELEMENT_${type.toUpperCase()}`:
        case `REQUEST_${type.toUpperCase()}`: {
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, true),
                ...changeErrorStatus(state, reducerType, false)
            };
            break;
        }
        case `RECEIVE_${type.toUpperCase()}`: {
            newState = {
                ...state,
                elements: getNewElements(state, action.data.data),
                collections: getNewCollections(state, reducerType, action.data),
                collectionMetadata: getNewMetadata(state, reducerType, action.data),
                similarAdsData: getNewElements(state, action.data.similarads_data, true),
                currentPageElements: getCurrentPageElements(action.data.data),
                userCreatedAt: getCurrentUserCreatedAt(state, action.data.data),
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }
        case `RECEIVE_ELEMENT_${type.toUpperCase()}`: {
            if (action.diffType) {
                reducerType = getType(action.diffType, action.url);
            }
            newState = {
                ...state,
                elements: getNewElements(state, [action.data.data]),
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }
        case `RESPONSE_PUT_${type.toUpperCase()}`:
        case `RESPONSE_PATCH_${type.toUpperCase()}`:
        case `RESPONSE_POST_${type.toUpperCase()}`: {
            newState = {
                ...state,
                collections: getNewCollections(state, reducerType, action.data),
                collectionMetadata: getNewMetadata(state, reducerType, action.data),
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }
        case `RESPONSE_PATCH_ELEMENT_${type.toUpperCase()}`:
        case `RESPONSE_PUT_ELEMENT_${type.toUpperCase()}`:
        case `RESPONSE_POST_ELEMENT_${type.toUpperCase()}`: {
            newState = {
                ...state,
                elements: getNewElements(state, [action.data.data]),
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }

        case `RESPONSE_DELETE_${type.toUpperCase()}`: {
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }
        case `RESET_${type.toUpperCase()}`: {
            return initialStateCentres;
        }
        default: {
            newState = state;
        }
    }

    if (state.metadataKey && (/RECEIVE_[A-Z_]*/ig.test(action.type) || /RESPONSE_[A-Z_]*/ig.test(action.type)) && action.data?.metadata && action.data.metadata[state.metadataKey]) {
        const metadataArray = Object.keys(action.data.metadata[state.metadataKey]).map(id => action.data.metadata[state.metadataKey][id]);
        const elements = {
            ...newState.elements,
            ...getNewElements(state, metadataArray)
        };

        return {
            ...newState,
            elements
        };
    }

    return newState;
};

const popularImgReducer = (state, action, type) => {
    let newState;

    const reducerType = getType(type, action.url);

    switch (action.type) {
        case `ERROR_${type.toUpperCase()}`:
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, false),
                ...changeErrorStatus(state, reducerType, true)
            };
            break;

        case `RECEIVE_${type.toUpperCase()}`: {
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, false)
            };
            break;
        }
        case `REQUEST_${type.toUpperCase()}`: {
            newState = {
                ...state,
                ...changeFetchingStatusNew(state, reducerType, true),
                ...changeErrorStatus(state, reducerType, false)
            };
            break;
        }

        default: {
            newState = state;
        }
    }

    return newState;
};

export {
    initialStateBase,
    reducer,
    popularImgReducer,
    changeFetchingStatusNew,
    changeErrorStatus
};
